import { useState, useEffect, useCallback } from 'react';
import { getQuizByCourseId, patchQuiz } from '../../services/content/quizService';
import type { Quiz } from '../../types/content/quiz';

const DEFAULT_PASS_THRESHOLD = 70;

interface QuizPassFailSettingsProps {
  courseId: string;
  quizId?: string | null;
  canEdit: boolean;
  onSaved?: () => void;
}

export function QuizPassFailSettings({
  courseId,
  quizId,
  canEdit,
  onSaved,
}: QuizPassFailSettingsProps) {
  const [quiz, setQuiz] = useState<Quiz | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const [title, setTitle] = useState('');
  const [passFailEnabled, setPassFailEnabled] = useState(false);
  const [passThreshold, setPassThreshold] = useState(DEFAULT_PASS_THRESHOLD);
  const [maxAttempts, setMaxAttempts] = useState<string>('');

  const inputClass = 'px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent text-sm disabled:bg-gray-50 disabled:text-gray-500';

  const applyQuiz = useCallback((q: Quiz) => {
    setQuiz(q);
    setTitle(q.title || '');
    setPassFailEnabled(!!q.passFailEnabled);
    setPassThreshold(
      q.passThreshold != null && q.passThreshold > 0
        ? q.passThreshold
        : DEFAULT_PASS_THRESHOLD,
    );
    setMaxAttempts(q.maxAttempts != null ? String(q.maxAttempts) : '');
  }, []);

  const fetchQuiz = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const q = await getQuizByCourseId(courseId);
      applyQuiz(q);
    } catch {
      setQuiz(null);
    } finally {
      setLoading(false);
    }
  }, [courseId, applyQuiz]);

  useEffect(() => {
    fetchQuiz();
  }, [fetchQuiz, quizId]);

  async function handleSave() {
    if (!quiz?.id || !canEdit) return;

    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      setError('Title is required');
      return;
    }

    let threshold = passThreshold;
    if (passFailEnabled) {
      if (!Number.isFinite(threshold) || threshold < 0 || threshold > 100) {
        setError('Pass threshold must be between 0 and 100');
        return;
      }
    }

    let attempts: number | null = null;
    if (passFailEnabled && maxAttempts.trim() !== '') {
      const parsed = parseInt(maxAttempts.trim(), 10);
      if (!Number.isFinite(parsed) || parsed < 1) {
        setError('Max attempts must be a positive number, or leave empty for unlimited');
        return;
      }
      attempts = parsed;
    }

    setError('');
    setSuccess(false);
    setSaving(true);
    try {
      const updated = await patchQuiz(quiz.id, {
        title: trimmedTitle,
        passFailEnabled,
        passThreshold: passFailEnabled ? threshold : null,
        maxAttempts: passFailEnabled ? attempts : null,
      });
      applyQuiz(updated);
      setSuccess(true);
      onSaved?.();
    } catch {
      setError('Failed to save quiz settings');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="border border-gray-200 rounded-lg px-3 py-2 mb-4">
        <p className="text-xs text-gray-400">Loading quiz settings...</p>
      </div>
    );
  }

  if (!quiz) {
    return (
      <div className="border border-gray-200 rounded-lg px-3 py-2 mb-4 flex items-center gap-2">
        <span className="text-xs font-medium text-gray-500">Quiz Settings</span>
        <span className="text-xs text-gray-400">No quiz linked to this course.</span>
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded-lg px-3 py-2 mb-4">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide shrink-0">Quiz</span>

        <input
          type="text"
          value={title}
          onChange={(e) => { setTitle(e.target.value); setSuccess(false); }}
          disabled={!canEdit}
          className={`${inputClass} flex-1 min-w-[12rem]`}
          placeholder="Title"
          title="Quiz title"
        />

        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 shrink-0">
          <label
            className={`inline-flex items-center gap-1 text-xs text-gray-600 shrink-0 ${passFailEnabled ? '' : 'invisible'}`}
            aria-hidden={!passFailEnabled}
          >
            Threshold
            <input
              type="number"
              min={0}
              max={100}
              value={passThreshold}
              onChange={(e) => {
                setPassThreshold(parseInt(e.target.value, 10) || 0);
                setSuccess(false);
              }}
              disabled={!canEdit || !passFailEnabled}
              tabIndex={passFailEnabled ? 0 : -1}
              className={`${inputClass} w-16`}
              title="Pass threshold %"
            />
            <span className="text-gray-400">%</span>
          </label>

          <label
            className={`inline-flex items-center gap-1 text-xs text-gray-600 shrink-0 ${passFailEnabled ? '' : 'invisible'}`}
            aria-hidden={!passFailEnabled}
          >
            Attempts
            <input
              type="number"
              min={1}
              value={maxAttempts}
              onChange={(e) => { setMaxAttempts(e.target.value); setSuccess(false); }}
              disabled={!canEdit || !passFailEnabled}
              tabIndex={passFailEnabled ? 0 : -1}
              className={`${inputClass} w-16`}
              placeholder="∞"
              title="Max attempts (empty = unlimited)"
            />
          </label>

          <label
            className={`inline-flex items-center gap-1.5 shrink-0 ${canEdit ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'}`}
            title="Enable Pass/Fail"
          >
            <span className="relative w-8 h-4 inline-block">
              <input
                type="checkbox"
                checked={passFailEnabled}
                disabled={!canEdit}
                onChange={() => {
                  setPassFailEnabled((v) => !v);
                  setSuccess(false);
                }}
                className="opacity-0 w-0 h-0 absolute"
              />
              <span
                className={`absolute inset-0 rounded-full transition-colors ${passFailEnabled ? 'bg-blue-600' : 'bg-gray-300'}`}
              />
              <span
                className={`absolute top-0.5 left-0.5 w-3 h-3 rounded-full bg-white shadow transition-transform ${passFailEnabled ? 'translate-x-4' : ''}`}
              />
            </span>
            <span className="text-xs text-gray-600">Pass/Fail</span>
          </label>

          {canEdit ? (
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || !title.trim()}
              className="px-2.5 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shrink-0"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          ) : (
            <span className="text-xs text-gray-400 shrink-0">View only</span>
          )}
        </div>
      </div>

      {(error || success) && (
        <p className={`text-xs mt-1.5 ${error ? 'text-red-600' : 'text-green-600'}`}>
          {error || 'Saved.'}
        </p>
      )}
    </div>
  );
}
